import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/services';
import { Link, useNavigate } from 'react-router-dom';
import { Spinner } from '@/components/ui/spinner';

import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/providers/auth-provider';
import AuthLayout from '@/layouts/auth-layout';
import { queryClient } from '@/providers/query-provider';
import { motion, AnimatePresence } from 'framer-motion';
import { Label } from '@/components/ui/label';
import { AlertCircle, CheckCircle } from 'lucide-react';
import { toastMessage } from '@/lib/utils';
import useHashSessionManager from '@/hooks/useAuthHash';

export const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { session } = useAuth();
  const { toast } = useToast();
  useHashSessionManager(() => {
    navigate('/login');
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) {
        setError(error.message);
      } else {
        const profile_update = await supabase
          .from('users')
          .update({ password_set: true })
          .eq('user_id', session?.user.id);
        if (profile_update.error) {
          setError(profile_update.error.message);
        } else {
          // If successful
          setIsResetting(false);
          setIsSuccess(true);
          toast({
            title: 'Password reset successful',
            description: 'Your password has been updated.',
            duration: 5000,
          });
          queryClient.invalidateQueries({ queryKey: ['profile', session?.user.id] });
          // Redirect after showing success animation
          setTimeout(() => {
            navigate('/');
          }, 3000);
        }
      }
    } catch (err) {
      console.error('Error updating password:', err);
      toastMessage(toast, 'Error', 'An unexpected error occurred', false);
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <form
        onSubmit={handleSubmit}
        className="space-y-4 w-full flex flex-col items-center"
      >
        <AnimatePresence mode="wait">
          {loading && <Spinner size={'large'} />}
          {!isSuccess ? (
            <motion.div
              key="password-form"
              className="max-w-sm w-full"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="space-y-2">
                <Label htmlFor="password">New Password</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isResetting}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm New Password</Label>
                <Input
                  id="confirm-password"
                  autoComplete="new-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={isResetting}
                  required
                />
              </div>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-red-500 text-sm flex items-center mt-2"
                >
                  <AlertCircle className="w-4 h-4 mr-2" />
                  {error}
                </motion.div>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="success-message"
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              className="flex flex-col items-center justify-center space-y-4 py-6"
            >
              <CheckCircle className="w-16 h-16 text-green-500" />
              <p className="text-xl font-semibold text-green-500">
                Password Reset Successful!
              </p>
              <p className="text-center text-sm text-muted-foreground">
                Your password has been updated. You will be redirected to the login page
                shortly.
              </p>
            </motion.div>
          )}
        </AnimatePresence>
        {error && <p className="text-red-500 text-sm">{error}</p>}
        <div className="flex flex-col items-center justify-center w-full">
          {!isSuccess && (
            <Button type="submit" className=" w-fit">
              Submit
            </Button>
          )}
          <Link to="/login" className=" underline mt-4 cursor-pointer text-center">
            Back to Login
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
};
